import {
	Alert,
	Box,
	Button,
	Card,
	CardContent,
	CardHeader,
	Chip,
	FormControl,
	FormControlLabel,
	InputLabel,
	MenuItem,
	Select,
	Stack,
	Switch,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Typography,
} from "@mui/material";
import {BreadcrumbItem, BreadcrumbLink, Breadcrumbs, ErrorAlert} from "@variocube/app-ui";
import {useMutation} from "@tanstack/react-query";
import React, {useCallback, useMemo, useRef, useState} from "react";
import {Link, Navigate, useNavigate} from "react-router-dom";
import {useTenantUserApi} from "../../api";
import {ImportUserRecordResult, ImportUsersResult} from "../../api/client.gen";
import {useLocalization} from "@smalox/shared";
import {useTenantFeature} from "../../tenant";
import {View, ViewHeader} from "../View";
// SheetJS CE 0.18.5 has known prototype-pollution CVEs (e.g. CVE-2023-30533).
// Risk is low here: we only parse user-selected files client-side, no formulas are evaluated,
// and no server-side execution occurs. Consider migrating to exceljs in a follow-up.
import * as XLSX from "xlsx";

type ImportAction = NonNullable<ImportUserRecordResult["action"]>;

type FieldKey = "foreignId" | "givenName" | "familyName" | "displayName" | "email" | "password";

const FIELDS: FieldKey[] = ["foreignId", "givenName", "familyName", "displayName", "email", "password"];

const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5 MB

const HEADER_PATTERNS: Record<FieldKey, RegExp> = {
	foreignId: /foreign.?id|extern|hr.?id|personal.?nummer|personnel|employee.?id|mitarbeiter/i,
	givenName: /given.?name|first.?name|vorname|pr[eé]nom/i,
	familyName: /family.?name|last.?name|sur.?name|nachname|nom.?de.?famille/i,
	displayName: /display.?name|full.?name|anzeigename|nom.?complet/i,
	email: /e?.?mail|email/i,
	password: /pass(word|wort)|mot.?de.?passe|kennwort/i,
};

function autoDetectMapping(headers: string[]): Record<FieldKey, number | null> {
	const mapping: Record<FieldKey, number | null> = {
		foreignId: null,
		givenName: null,
		familyName: null,
		displayName: null,
		email: null,
		password: null,
	};

	for (const field of FIELDS) {
		for (let i = 0; i < headers.length; i++) {
			if (HEADER_PATTERNS[field].test(headers[i])) {
				// Avoid double-mapping: displayName should not match if givenName or familyName already matched
				const alreadyMapped = Object.entries(mapping).some(
					([key, val]) => val === i && key !== field,
				);
				if (!alreadyMapped) {
					mapping[field] = i;
					break;
				}
			}
		}
	}

	return mapping;
}

export function UserImport() {
	const {t} = useLocalization();
	const canImportUsers = useTenantFeature("USER_IMPORT");
	const {importUsers} = useTenantUserApi();
	const navigate = useNavigate();

	const [step, setStep] = useState<1 | 2 | 3>(1);
	const [headers, setHeaders] = useState<string[]>([]);
	const [rows, setRows] = useState<string[][]>([]);
	const [mapping, setMapping] = useState<Record<FieldKey, number | null>>({
		foreignId: null,
		givenName: null,
		familyName: null,
		displayName: null,
		email: null,
		password: null,
	});
	const [deleteAbsent, setDeleteAbsent] = useState(false);
	const [result, setResult] = useState<ImportUsersResult | null>(null);
	const [showErrorsOnly, setShowErrorsOnly] = useState(false);
	const [fileError, setFileError] = useState<string | null>(null);
	const fileInputRef = useRef<HTMLInputElement>(null);

	const handleFile = useCallback((file: File) => {
		setFileError(null);

		if (file.size > MAX_FILE_SIZE) {
			setFileError(t("users.import.fileTooLarge"));
			return;
		}

		const reader = new FileReader();
		reader.onload = (e) => {
			const data = new Uint8Array(e.target?.result as ArrayBuffer);
			const workbook = XLSX.read(data, {type: "array"});
			const sheet = workbook.Sheets[workbook.SheetNames[0]];
			const json: string[][] = XLSX.utils.sheet_to_json(sheet, {header: 1, defval: ""});

			if (json.length < 2) {
				setFileError(t("users.import.emptyFile"));
				return;
			}

			const fileHeaders = json[0].map(String);
			const fileRows = json.slice(1).filter(row => row.some(cell => String(cell).trim()));

			setHeaders(fileHeaders);
			setRows(fileRows.map(row => row.map(String)));
			setMapping(autoDetectMapping(fileHeaders));
			setStep(2);
		};
		reader.readAsArrayBuffer(file);
	}, []);

	const handleFileInput = useCallback(
		(e: React.ChangeEvent<HTMLInputElement>) => {
			const file = e.target.files?.[0];
			if (file) handleFile(file);
		},
		[handleFile],
	);

	const handleDrop = useCallback(
		(e: React.DragEvent) => {
			e.preventDefault();
			const file = e.dataTransfer.files?.[0];
			if (file) handleFile(file);
		},
		[handleFile],
	);

	const canSubmit = mapping.givenName !== null && mapping.familyName !== null;

	const previewRows = useMemo(() => rows.slice(0, 10), [rows]);

	const getMappedValue = useCallback(
		(row: string[], field: FieldKey): string => {
			const idx = mapping[field];
			return idx !== null ? (row[idx] || "") : "";
		},
		[mapping],
	);

	const submit = useMutation({
		mutationFn: async () => {
			const records = rows.map(row => ({
				foreignId: getMappedValue(row, "foreignId") || undefined,
				givenName: getMappedValue(row, "givenName"),
				familyName: getMappedValue(row, "familyName"),
				displayName: getMappedValue(row, "displayName") || undefined,
				email: getMappedValue(row, "email") || undefined,
				password: getMappedValue(row, "password") || undefined,
				forcePasswordChange: !getMappedValue(row, "password"),
			}));

			return importUsers({records, deleteAbsent});
		},
		onSuccess: (importResult) => {
			setResult(importResult);
			setStep(3);
		},
	});

	const downloadTemplate = useCallback(() => {
		const wb = XLSX.utils.book_new();
		const wsData = [
			["foreignId", "givenName", "familyName", "displayName", "email", "password"],
			["EMP001", "John", "Doe", "John Doe", "john.doe@example.com", ""],
			["EMP002", "Jane", "Smith", "Jane Smith", "jane.smith@example.com", ""],
		];
		const ws = XLSX.utils.aoa_to_sheet(wsData);
		XLSX.utils.book_append_sheet(wb, ws, "Users");
		XLSX.writeFile(wb, "user_import_template.xlsx");
	}, []);

	const updateMapping = useCallback(
		(field: FieldKey, value: number | null) => {
			setMapping(prev => ({...prev, [field]: value}));
		},
		[],
	);

	const actionColor = (action: ImportAction): "success" | "info" | "warning" | "error" | "default" => {
		switch (action) {
			case "CREATED":
				return "success";
			case "UPDATED":
				return "info";
			case "DELETED":
				return "warning";
			case "SKIPPED":
				return "default";
		}
	};

	const filteredResults = useMemo(() => {
		if (!result) return [];
		if (showErrorsOnly) return (result.results ?? []).filter(r => !r.success);
		return result.results ?? [];
	}, [result, showErrorsOnly]);

	if (canImportUsers === false) {
		return <Navigate to=".." />;
	}

	return (
		<View>
			<Breadcrumbs>
				<BreadcrumbLink component={Link} to="..">{t("users.title")}</BreadcrumbLink>
				<BreadcrumbItem>{t("users.import.title")}</BreadcrumbItem>
			</Breadcrumbs>
			<ViewHeader title={t("users.import.title")} />

			{step === 1 && (
				<Card>
					<CardHeader title={t("users.import.upload")} />
					<CardContent>
						<Stack spacing={2}>
							<Button variant="outlined" onClick={downloadTemplate}>
								{t("users.import.downloadTemplate")}
							</Button>
							{fileError && <Alert severity="error">{fileError}</Alert>}
							<Box
								onDrop={handleDrop}
								onDragOver={e => e.preventDefault()}
								sx={{
									border: "2px dashed",
									borderColor: "divider",
									borderRadius: 1,
									p: 4,
									textAlign: "center",
									cursor: "pointer",
								}}
								onClick={() => fileInputRef.current?.click()}
							>
								<Typography color="text.secondary">
									{t("users.import.dropzone")}
								</Typography>
								<input
									ref={fileInputRef}
									type="file"
									accept=".xlsx,.xls,.csv"
									onChange={handleFileInput}
									style={{display: "none"}}
								/>
							</Box>
						</Stack>
					</CardContent>
				</Card>
			)}

			{step === 2 && (
				<Card>
					<CardHeader
						title={t("users.import.mapping")}
						subheader={t("users.import.totalRows", {count: rows.length})}
					/>
					<CardContent>
						<Stack spacing={2}>
							<Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
								{FIELDS.map(field => (
									<FormControl key={field} size="small" sx={{minWidth: 180}}>
										<InputLabel>{t(`users.import.fields.${field}`)}</InputLabel>
										<Select
											value={mapping[field] ?? ""}
											label={t(`users.import.fields.${field}`)}
											onChange={e => {
												const val = e.target.value;
												updateMapping(field, val === "" ? null : Number(val));
											}}
										>
											<MenuItem value="">
												<em>—</em>
											</MenuItem>
											{headers.map((header, idx) => (
												<MenuItem key={idx} value={idx}>
													{header}
												</MenuItem>
											))}
										</Select>
									</FormControl>
								))}
							</Stack>

							{!canSubmit && (
								<Alert severity="warning">{t("users.import.requiredFields")}</Alert>
							)}

							<Typography variant="subtitle2">{t("users.import.preview")}</Typography>
							<TableContainer>
								<Table size="small">
									<TableHead>
										<TableRow>
											{FIELDS.filter(f => mapping[f] !== null).map(f => (
												<TableCell key={f}>{t(`users.import.fields.${f}`)}</TableCell>
											))}
										</TableRow>
									</TableHead>
									<TableBody>
										{previewRows.map((row, idx) => (
											<TableRow key={idx}>
												{FIELDS.filter(f => mapping[f] !== null).map(f => (
													<TableCell key={f}>{getMappedValue(row, f)}</TableCell>
												))}
											</TableRow>
										))}
									</TableBody>
								</Table>
							</TableContainer>

							{submit.error && <ErrorAlert error={submit.error} />}

							<FormControlLabel
								control={
									<Switch
										checked={deleteAbsent}
										onChange={e => setDeleteAbsent(e.target.checked)}
									/>
								}
								label={t("users.import.deleteAbsent")}
							/>
							<Typography variant="caption" color="text.secondary">
								{t("users.import.deleteAbsentHelperText")}
							</Typography>

							<Stack direction="row" spacing={2} alignItems="center">
								<Button
									variant="contained"
									onClick={() => submit.mutate()}
									disabled={!canSubmit || submit.isLoading}
								>
									{t("users.import.submit", {count: rows.length})}
								</Button>
								<Button variant="outlined" onClick={() => setStep(1)}>
									{t("users.import.back")}
								</Button>
							</Stack>
						</Stack>
					</CardContent>
				</Card>
			)}

			{step === 3 && result && (
				<Card>
					<CardHeader title={t("users.import.results")} />
					<CardContent>
						<Stack spacing={2}>
							<Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
								{(result.created ?? 0) > 0 && (
									<Chip label={`${t("users.import.action.CREATED")}: ${result.created}`} color="success" />
								)}
								{(result.updated ?? 0) > 0 && (
									<Chip label={`${t("users.import.action.UPDATED")}: ${result.updated}`} color="info" />
								)}
								{(result.deleted ?? 0) > 0 && (
									<Chip label={`${t("users.import.action.DELETED")}: ${result.deleted}`} color="warning" />
								)}
								{(result.skipped ?? 0) > 0 && (
									<Chip label={`${t("users.import.action.SKIPPED")}: ${result.skipped}`} />
								)}
								{(result.failed ?? 0) > 0 && (
									<Chip label={`${t("users.import.action.FAILED")}: ${result.failed}`} color="error" />
								)}
							</Stack>

							{(result.failed ?? 0) > 0 && (
								<Button
									size="small"
									variant="text"
									onClick={() => setShowErrorsOnly(!showErrorsOnly)}
								>
									{showErrorsOnly
										? t("users.import.showAll")
										: t("users.import.showErrorsOnly")}
								</Button>
							)}

							<TableContainer>
								<Table size="small">
									<TableHead>
										<TableRow>
											<TableCell>#</TableCell>
											<TableCell>{t("users.import.fields.action")}</TableCell>
											<TableCell>{t("users.username")}</TableCell>
											<TableCell>{t("users.displayName")}</TableCell>
											<TableCell>{t("users.email")}</TableCell>
											<TableCell>{t("users.import.fields.foreignId")}</TableCell>
											<TableCell>{t("users.import.fields.error")}</TableCell>
										</TableRow>
									</TableHead>
									<TableBody>
										{filteredResults.map((r, idx) => (
											<TableRow key={idx}>
												<TableCell>{r.rowNumber || "—"}</TableCell>
												<TableCell>
													<Chip
														label={t(`users.import.action.${!r.success ? "FAILED" : (r.action ?? "SKIPPED")}`)}
														color={!r.success ? "error" : actionColor(r.action ?? "SKIPPED")}
														size="small"
													/>
												</TableCell>
												<TableCell>{r.username}</TableCell>
												<TableCell>{r.displayName}</TableCell>
												<TableCell>{r.email}</TableCell>
												<TableCell>{r.foreignId}</TableCell>
												<TableCell>
													{r.errorMessage && `${r.errorCode}: ${r.errorMessage}`}
												</TableCell>
											</TableRow>
										))}
									</TableBody>
								</Table>
							</TableContainer>

							<Stack direction="row" spacing={2}>
								<Button variant="contained" onClick={() => navigate("..")}>
									{t("users.import.backToUsers")}
								</Button>
								<Button
									variant="outlined"
									onClick={() => {
										setStep(1);
										setResult(null);
										setRows([]);
										setHeaders([]);
									}}
								>
									{t("users.import.importAnother")}
								</Button>
							</Stack>
						</Stack>
					</CardContent>
				</Card>
			)}
		</View>
	);
}
